// TypeForm.jsx (updated header section)
import React, { useState, useEffect } from 'react';
import { 
  Keyboard, 
  Sun, 
  Moon, 
  Clock, 
  Languages, 
  Gauge,
  Brain,
  Target,
  Zap,
  Eye,
  BarChart,
  Feather,
  Award,
  Briefcase
} from "lucide-react";
import './TypeForm.css';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import examConfig from "../src/config/examConfig";

export default function TypeForm() {
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("typeace-theme") || "light";
  });
  const [mobileView, setMobileView] = useState('practice');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const navi = useNavigate();

  const themes = ["light", "dark", "neon"];
  const cycleTheme = () => {
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    setTheme(themes[nextIndex]);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 900);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("typeace-theme", theme);
  }, [theme]);

  const practiceModes = [
    {
      id: 1,
      name: "Classic",
      title: "TypeAce Classic",
      description: "Practice Modes • Choose own time",
      icon: <Keyboard size={24} />,
      duration: "Custom",
      stats: "Unlimited practice",
      color: "#4a6b8a"
    },
    {
      id: 2,
      name: "Zen Mode",
      title: "Zen Mode",
      description: "No timer • No scoring • Pure focus",
      icon: <Brain size={24} />,
      duration: "∞",
      stats: "Mindfulness typing",
      color: "#6b8a7a"
    },
    {
      id: 3,
      name: "Speed Burst",
      title: "Speed Burst",
      description: "30s sprint • Max WPM challenge",
      icon: <Zap size={24} />,
      duration: "30s",
      stats: "Peak performance",
      color: "#c9a34b"
    },
    {
      id: 4,
      name: "Precision Mode",
      title: "Precision Mode",
      description: "Accuracy-focused • 95% minimum",
      icon: <Target size={24} />,
      duration: "60s",
      stats: "95% target",
      color: "#9b6b8a"
    },
    {
      id: 5,
      name: "Blind Mode",
      title: "Blind Mode",
      description: "Memorize & type • Memory challenge",
      icon: <Eye size={24} />,
      duration: "45s",
      stats: "Memory test",
      color: "#8a6b9b"
    },
    {
      id: 6,
      name: "Progressive Mode",
      title: "Progressive Mode",
      description: "Increasing difficulty • Adaptive text",
      icon: <BarChart size={24} />,
      duration: "90s",
      stats: "Adaptive learning",
      color: "#b88a4a"
    }
  ];

  const examModes = Object.values(examConfig);
  
  const handleStartPractice = (mode) => {
    if (mode.name === "Classic") {
      navi("/classic");
    } else {
      navi(`/practice/${mode.name.toLowerCase().replace(/\s+/g, "-")}`);
    }
  };
  
  const handleStartExam = (exam) => {
    console.log(exam.examId);
    navi(`/test-setup/${exam.examId}`, {
    state: { exam }
  })};

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{
        duration: 0.3,
        ease: "easeInOut"
      }}
    >
    <div className="typeform">
      <div className="typeform-container">
        <header className="typeform-header">
          <button onClick={cycleTheme} className="theme-toggle">
            {theme === "light" && <Sun size={18} />}
            {theme === "dark" && <Moon size={18} />}
            {theme === "neon" && <Zap size={18} />}
          </button>
          <div className="header-content">
            <Keyboard size={24} className="header-icon" />
            <h1>
              <span onClick={() => {navi("/")}} className="desktop-title">TypeAce – SPEED. PRECISION. MASTERY.</span>
              <span onClick={() => {navi("/")}} className="mobile-title">TypeAce</span>
            </h1>
          </div>
        </header>
        <div className="content">
          {/* Mobile View Toggle */}
          {isMobile && (
            <div className="mobile-toggle-container">
              <button 
                className={`mobile-toggle-btn ${mobileView === 'practice' ? 'active' : ''}`}
                onClick={() => setMobileView('practice')}
              >
                <Brain size={16} />
                <span>Practice</span>
              </button>
              <button 
                className={`mobile-toggle-btn ${mobileView === 'exam' ? 'active' : ''}`}
                onClick={() => setMobileView('exam')}
              >
                <Briefcase size={16} />
                <span>Exam</span>
              </button>
            </div>
          )}

          {/* Desktop: Both sections side by side */}
          {/* Mobile: Only one section based on toggle */}
          <div className='mode-wrapper'>
            {/* Practice & Skill Modes Section */}
            {/* {(!isMobile || mobileView === 'practice') && (
              <section className={`mode-section practice-section ${isMobile && mobileView !== 'practice' ? 'hidden' : ''}`}>
                <div className="section-header">
                  <Brain size={20} className="section-icon" />
                  <h2 className="section-title">Practice & Skill Modes</h2>
                  <span className="section-badge">6 modes</span>
                </div>
                <div className="practice-grid">
                  {practiceModes.map((mode) => (
                    <div key={mode.id} className="practice-card" style={{ borderTopColor: mode.color }}>
                      <div className="practice-card-header">
                        <span className="practice-icon" style={{ background: `${mode.color}15`, color: mode.color }}>
                          {mode.icon}
                        </span>
                        <span className="practice-badge">{mode.name}</span>
                      </div>
                      
                      <h3 className="practice-title">{mode.title}</h3>
                      <p className="practice-description">{mode.description}</p>
                      
                      <div className="practice-stats">
                        <div className="practice-stat">
                          <Clock size={14} />
                          <span>{mode.duration}</span>
                        </div>
                        <div className="practice-stat">
                          <Feather size={14} />
                          <span>{mode.stats}</span>
                        </div>
                      </div>

                      <button 
                        onClick={() => handleStartPractice(mode)}
                        className="practice-btn"
                      >
                        Start Practice
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )} */}

            {/* Government Exam Modes Section */}
            {(!isMobile || mobileView === 'exam') && (
              <section className={`mode-section exam-section ${isMobile && mobileView !== 'exam' ? 'hidden' : ''}`}>
                <div className="section-header">
                  <Briefcase size={20} className="section-icon" />
                  <h2 className="section-title">Exam Modes</h2>
                  <span className="section-badge">{examModes.length} exams</span>
                </div>

                <div className="exam-grid">
                  {examModes.map((exam) => (
                    <div key={exam.examId} className="exam-card">
                      <div className="exam-card-header">
                        <div className="exam-title-section">
                          <h3 className="exam-name">{exam.examName}</h3>
                          <span className="exam-code">{exam.examCode}</span>
                        </div>
                        <Award size={20} className="exam-icon" />
                      </div>
                      
                      <div className="exam-details">
                        <div className="exam-detail-row">
                          <Clock size={14} />
                          <span>Duration: <strong>{exam.duration} min</strong></span>
                        </div>
                        
                        <div className="exam-detail-row">
                          <Target size={14} />
                          <span>Required: <strong>
                            {exam.type === "keystroke"
                              ? `${exam.minKeyDepressions} Key Depressions`
                              : `${exam.minWPM} WPM`}
                            </strong></span>
                        </div>
                        
                        <div className="exam-detail-row">
                          <Languages size={14} />
                          <span>Language: <strong>{exam.language}</strong></span>
                        </div>

                        <div className="exam-detail-row">
                          <Gauge size={14} />
                          <span>Total Marks: <strong>{exam.totalMarks}</strong></span>
                        </div>
                      </div>

                      <div className="exam-footer">
                        <button 
                          onClick={() => handleStartExam(exam)}
                          className="exam-btn"
                        >
                          Start Test
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
          
          <div className="info-note">
            <p>Choose from creative practice modes to build your skills, or select a structured exam mode for official test preparation. All progress is tracked in real-time.</p>
          </div>
        </div>
      </div>
    </div>
    </motion.div>
  );
}